import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';

interface Member {
  id: number;
  name: string;
  member_no: string;
  phone_number: string;
  id_no?: string;
}

interface LoanArrear {
  loan_id: number;
  loan_no: string;
  member: Member;
  loan_type: string;
  principal: number;
  total_amount: number;
  total_repaid: number;
  balance_not_due: number;
  balance_overdue: number;
  total_balance: number;
  status: string;
  start_date: string;
  end_date: string;
  days_overdue: number;
  expected_payments: number;
  actual_payments: number;
  missed_payments: number;
  monthly_installment: number;
  is_erroneous: boolean;
}

interface ArrearsSummary {
  total_defaulters: number;
  total_arrears_amount: number;
  total_principal_at_risk: number;
  total_expected_amount: number;
  total_repaid: number;
  erroneous_loans: number;
}

interface LoanArrearsResponse {
  success: boolean;
  data: LoanArrear[];
  summary: ArrearsSummary;
}

interface LoanArrearsFilters {
  member_id?: number;
  loan_type_id?: number;
  min_arrears?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

interface PaymentScheduleItem {
  month: number;
  payment_date: string;
  expected_amount: number;
  paid_amount: number;
  shortfall: number;
  is_overdue: boolean;
  status: string;
}

interface RepaymentHistoryItem {
  id: number;
  amount: number;
  payment_date: string;
  payment_method: string;
  description: string;
}

interface LoanArrearDetail {
  loan_id: number;
  loan_no: string;
  member: Member;
  loan_type: string;
  principal: number;
  total_amount: number;
  total_repaid: number;
  arrears_amount: number;
  status: string;
  start_date: string;
  end_date: string;
  days_overdue: number;
  is_erroneous: boolean;
  payment_schedule: PaymentScheduleItem[];
  repayment_history: RepaymentHistoryItem[];
}

interface MemberArrearsSummary {
  member_id: number;
  name: string;
  member_no: string;
  phone_number: string;
  id_no?: string;
  total_arrears: number;
  loans_in_arrears: number;
  credit_limit: number;
}

interface FixErroneousResult {
  loan_id: number;
  loan_no: string;
  member_name: string;
  old_balance: number;
  new_balance: number;
  new_status: string;
}

interface FixErroneousResponse {
  success: boolean;
  message: string;
  fixed: FixErroneousResult[];
  failed: any[];
  summary: {
    total_processed: number;
    fixed_count: number;
    failed_count: number;
  };
}

export const useLoanArrears = (filters: LoanArrearsFilters = {}) => {
  return useQuery<LoanArrearsResponse>({
    queryKey: ['loan-arrears', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
      
      const { data } = await api.get(`/loan-arrears?${params.toString()}`);
      return data;
    },
  });
};

export const useLoanArrearDetail = (loanId: number | null) => {
  return useQuery<{ success: boolean; data: LoanArrearDetail }>({
    queryKey: ['loan-arrear-detail', loanId],
    queryFn: async () => {
      if (!loanId) throw new Error('Loan ID is required');
      const { data } = await api.get(`/loan-arrears/${loanId}`);
      return data;
    },
    enabled: !!loanId,
  });
};

export const useArrearsSummaryByMember = () => {
  return useQuery<{ success: boolean; data: MemberArrearsSummary[]; summary: { total_defaulters: number; total_arrears: number } }>({
    queryKey: ['arrears-summary-by-member'],
    queryFn: async () => {
      const { data } = await api.get('/loan-arrears/summary-by-member');
      return data;
    },
  });
};

export const useFixErroneousLoans = () => {
  const queryClient = useQueryClient();

  return useMutation<FixErroneousResponse>({
    mutationFn: async () => {
      const { data } = await api.post('/loan-arrears/fix-erroneous');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loan-arrears'] });
      queryClient.invalidateQueries({ queryKey: ['arrears-summary-by-member'] });
    },
  });
};
